import React, { useState, cloneElement } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useInteractions,
  useDismiss,
  useRole,
  FloatingPortal,
} from '@floating-ui/react';

/* Shared tooltip — dark-themed, Floating UI positioning (no clipping at
   viewport edges), keyboard-accessible (shows on focus), consistent styling
   across the app. Replaces inconsistent native `title=` attributes for
   hints that deserve proper visual treatment.

   Two usage patterns:
   1. Simple label (string): <Tooltip label="Hit ? for shortcuts"><button>…</button></Tooltip>
   2. Rich content (JSX):     <Tooltip content={<RichExplanation/>}><span>…</span></Tooltip>

   Rich mode enables pointer-events on the tooltip surface so users can
   scroll long explanations; simple label mode keeps pointer-events
   disabled for lighter hover behaviour. */
export default function Tooltip({
  label, content, children,
  placement = 'top', delay = 200,
  maxWidth = 320,
  rich = false,
}) {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { delay: { open: delay, close: 0 }, move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const body = content != null ? content : label;
  if (body == null) return children;
  const isRich = rich || content != null;
  const maxWidthValue = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

  /* Inject ref+props onto the single child without adding a wrapper span
     that would shift layout. */
  const trigger = cloneElement(children, {
    ref: refs.setReference,
    ...getReferenceProps({ ...children.props }),
  });

  return (
    <>
      {trigger}
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              maxWidth: `min(${maxWidthValue}, calc(100vw - 16px))`,
              maxHeight: 'min(60vh, 420px)',
              overflowWrap: 'anywhere',
            }}
            {...getFloatingProps()}
            className={`z-[200] overflow-y-auto overflow-x-hidden bg-[rgba(15,23,42,0.97)] text-[#e2e8f0] border border-[#475569] rounded-md text-[12px] leading-[1.5] shadow-[0_10px_28px_rgba(0,0,0,0.55),0_3px_8px_rgba(0,0,0,0.4)] backdrop-blur-sm ${isRich ? 'px-[0.75rem] py-[0.55rem] pointer-events-auto' : 'px-[0.55rem] py-[0.35rem] pointer-events-none'}`}
          >
            {body}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

/* Rich explanation block — standardized layout for formula + long
   explanation + benchmark hint. Pass to <Tooltip content={…}/>. */
export function RichExplanation({ title, formula, hint, explanation, direction, betterWhen }) {
  /* v8 Path AM — direction guide. When a metric declares `direction`
     and `betterWhen`, render a visual polarity legend at the bottom
     of the tooltip so users know instantly what rising/falling means
     for THIS metric. Color matches the sparkline tone:
       - betterWhen='higher' → ↗ green (favorable), ↘ red (unfavorable)
       - betterWhen='lower'  → ↗ red, ↘ green
       - betterWhen='neutral' → ↕ single gray explanation
     Accepts direction as either a string (neutral one-liner) or an
     object { whenUp, whenDown, neutral? }. */
  const hasDirection = !!(direction && (direction.whenUp || direction.whenDown || direction.neutral || typeof direction === 'string'));
  const neutralText = typeof direction === 'string' ? direction : direction?.neutral;
  const isNeutralMetric = betterWhen === 'neutral' || !betterWhen || (neutralText && !direction?.whenUp && !direction?.whenDown);
  const upIsGood = betterWhen === 'higher';
  return (
    <div className="flex flex-col gap-[0.35rem]">
      {title && <strong className="text-accent-gold text-[13px] block">{title}</strong>}
      {formula && (
        <div className="text-[11px] text-text-muted font-mono border border-border rounded px-[6px] py-[3px] bg-black/20">
          {formula}
        </div>
      )}
      {hint && <div className="text-[12px] text-[#e2e8f0] italic">{hint}</div>}
      {explanation && <div className="text-[12px] text-[#e2e8f0] leading-[1.55]">{explanation}</div>}
      {hasDirection && (
        <div className="flex flex-col gap-[4px] mt-[3px] pt-[7px] border-t border-white/[0.08]">
          <div className="text-[9.5px] uppercase tracking-[0.1em] text-text-dim font-semibold">Direction guide</div>
          {isNeutralMetric && neutralText ? (
            <div className="flex items-start gap-[6px] text-[11px]">
              <span className="text-text-dim font-semibold whitespace-nowrap">↕ Neutral</span>
              <span className="text-[#e2e8f0] leading-[1.45]">{neutralText}</span>
            </div>
          ) : (
            <>
              {direction.whenUp && (
                <div className="flex items-start gap-[6px] text-[11px]">
                  <span className={`font-semibold whitespace-nowrap ${upIsGood ? 'text-accent-green' : 'text-accent-red'}`}>
                    {upIsGood ? '↗ Favorable' : '↗ Unfavorable'}
                  </span>
                  <span className="text-[#e2e8f0] leading-[1.45]">{direction.whenUp}</span>
                </div>
              )}
              {direction.whenDown && (
                <div className="flex items-start gap-[6px] text-[11px]">
                  <span className={`font-semibold whitespace-nowrap ${upIsGood ? 'text-accent-red' : 'text-accent-green'}`}>
                    {upIsGood ? '↘ Unfavorable' : '↘ Favorable'}
                  </span>
                  <span className="text-[#e2e8f0] leading-[1.45]">{direction.whenDown}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
